/**
 * One destination row inside the profile panel — shared shape for both the
 * prominent primary nav (Wardrobe, Style Studio, ...) and the quieter
 * secondary rows (Account Details, Feedback, ...), which is what actually
 * produces the visual hierarchy the spec asks for: same component, `tone`
 * changes the weight rather than two different row designs drifting apart.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';

export default function ProfileNavRow({
  icon: Icon, art, label, sublabel, badge, onClick, tone = 'primary', testId,
}) {
  const primary = tone === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-slate-50 active:scale-[0.99] transition-transform text-left"
    >
      {/* `art` is for a row whose destination has a mark of its own (the
          Wardrobe). It replaces the tinted circle rather than sitting inside
          it: nesting one circle in another reads as a badge on an icon, not
          as the thing itself. */}
      {art ? (
        <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 ring-1 ring-slate-100">
          {art}
        </div>
      ) : (
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          primary ? 'bg-mahiteal-50' : 'bg-slate-100'
        }`}
        >
          <Icon className={`w-[18px] h-[18px] ${primary ? 'text-mahiteal-600' : 'text-slate-500'}`} strokeWidth={1.75} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={`font-mahi-sans leading-tight ${primary ? 'text-sm font-semibold text-slate-900' : 'text-[13px] font-medium text-slate-700'}`}>
          {label}
        </p>
        {sublabel && (
          <p className="text-xs text-slate-400 font-mahi-sans truncate mt-0.5">{sublabel}</p>
        )}
      </div>
      {badge && (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-mahiteal-700 bg-mahiteal-50 px-2 py-0.5 rounded-full shrink-0">
          {badge}
        </span>
      )}
      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
    </button>
  );
}
